import { useCallback, useState } from 'react';
import useSWRInfinite from 'swr/infinite';
import { ImageMetadata, PexelsSearchResponse, PixabaySearchResponse } from 'types/imageLibsTypes';

import { combineImageSearchResults } from './useImageSearch.helpers';

const PAGE_SIZE = 50;

const PIXABAY_SEARCH_API = '/infographics/proxy_search_pixabay';
export const PEXELS_SEARCH_API = '/infographics/proxy_search_pexels';

const fetcher = (url: string) => fetch(url).then((httpResponse) => httpResponse.json());

export const useImageSearch = () => {
  const [query, setQuery] = useState<string>('');

  const getKey = (libUri: string) => (pageIndex: number) => {
    if (query === '') return null;

    const queryObj = {
      keywords: query,
      per_page: PAGE_SIZE.toString(),
      page: `${pageIndex + 1}`,
    };

    const queryStr = `?${new URLSearchParams(queryObj).toString()}`;

    return `${libUri}${queryStr}`;
  };

  const swrConfig = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  };

  const {
    size: pixabaySize,
    setSize: setPixabaySize,
    data: pixabayData,
    error: pixabayError,
  } = useSWRInfinite<PixabaySearchResponse>(getKey(PIXABAY_SEARCH_API), fetcher, swrConfig);
  const {
    size: pexelsSize,
    setSize: setPexelsSize,
    data: pexelsData,
    error: pexelsError,
  } = useSWRInfinite<PexelsSearchResponse>(getKey(PEXELS_SEARCH_API), fetcher, swrConfig);

  const isLoadingMorePixabayImage =
    (!pixabayData && !pixabayError && !!query) ||
    (pixabaySize > 0 && !!pixabayData && typeof pixabayData[pixabaySize - 1] === 'undefined');
  const isLoadingMorePexelsImage =
    (!pexelsData && !pexelsError && !!query) ||
    (pexelsSize > 0 && !!pexelsData && typeof pexelsData[pexelsSize - 1] === 'undefined');
  const isLoadingMore = isLoadingMorePexelsImage || isLoadingMorePixabayImage;

  const nextPage = useCallback(() => {
    setPixabaySize((_size) => _size + 1);
    setPexelsSize((_size) => _size + 1);
  }, [setPexelsSize, setPixabaySize]);

  const prevPage = useCallback(() => {
    setPixabaySize((_size) => (_size === 1 ? 1 : _size - 1));
    setPexelsSize((_size) => (_size === 1 ? 1 : _size - 1));
  }, [setPexelsSize, setPixabaySize]);

  const clearSearch = useCallback(() => {
    setQuery('');
  }, []);

  const combinedData: ImageMetadata[] = combineImageSearchResults(pixabayData, pexelsData);

  return {
    data: combinedData,
    error: pexelsError ?? pixabayError,
    isLoadingMore,
    query,
    clearSearch,
    setQuery,
    nextPage,
    prevPage,
    PAGE_SIZE,
  };
};
